/**
 * Business-error model for the Login flow.
 *
 * Discriminated union: the generic `network` / `api` transport failures (from
 * the app-wide `@/utils/infra-error` classifier) plus the form-specific
 * `validation` variant (field-level messages produced by `validateCredentials`).
 *
 * The server message IS surfaced to the user (like reset-password): the caller
 * is submitting credentials they already hold, so there is nothing to enumerate.
 * The rate-limit `retryAfter` is deliberately NOT part of this model; it is a
 * UI-cooldown concern carried separately in slice state, extracted here by the
 * pure `retryAfterOf` helper.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

export type LoginError =
  | InfraFailure
  | { kind: 'validation'; fields: Record<string, string> };

/**
 * Map an infrastructure error into a domain `LoginError`. Only ever yields a
 * `network` / `api` failure; `validation` errors are produced directly by
 * `validateCredentials`.
 */
export function toLoginError(e: unknown): LoginError {
  return classifyInfraError(e, 'Invalid email or password.');
}

/**
 * Pure, duck-typed extraction of the api client's `ApiError.retryAfter` (a
 * throttling cooldown in seconds). Returns 0 when absent; never depends on the
 * concrete `ApiError` class, so the domain stays framework-free.
 */
export function retryAfterOf(e: unknown): number {
  const v = (e as { retryAfter?: unknown })?.retryAfter;
  return typeof v === 'number' && v > 0 ? v : 0;
}
